import { useState, useEffect, useCallback, KeyboardEvent } from 'react';
import {
  DialogContent, DialogActions, Button, Alert, Box, CircularProgress,
  Typography, TextField, MenuItem, Select, FormControl, InputLabel,
  SelectChangeEvent, Chip,
} from '@mui/material';
import DraggableDialog from '../../components/DraggableDialog';
import { CopyId } from '../../components/CopyId';
import { Send } from '@mui/icons-material';
import { Formik } from 'formik';
import { WorkspaceField, defaultCreateWorkspace } from '../../components/WorkspaceField';
import * as Yup from 'yup';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useModalState } from '../../hooks/useModalState';
import { useDirtyFormGuard } from '../../hooks/useDirtyFormGuard';
import FormDirtyTracker from '../../components/FormDirtyTracker';
import { FormikSwitch } from '../../components/FormikFields';
import {
  alertChannelsService, AlertChannelType, AlertChannelConfig,
  EmailChannelConfig, SlackChannelConfig, TelegramChannelConfig,
} from '../../services/alert-channels.service';

const CHANNEL_TYPES: { value: AlertChannelType; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'slack', label: 'Slack' },
  { value: 'telegram', label: 'Telegram' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const schema = Yup.object({
  name: Yup.string().required('Name is required'),
  type: Yup.string().required('Type is required'),
  enabled: Yup.boolean(),
});

interface EditableConfig {
  recipients: string[];
  webhook_url: string;
  bot_token: string;
  chat_id: string;
}

const DEFAULT_CONFIG: EditableConfig = { recipients: [], webhook_url: '', bot_token: '', chat_id: '' };

function configFrom(type: AlertChannelType, cfg: AlertChannelConfig | undefined): EditableConfig {
  const next = { ...DEFAULT_CONFIG };
  if (!cfg) return next;
  if (type === 'email') next.recipients = (cfg as EmailChannelConfig).recipients || [];
  if (type === 'slack') next.webhook_url = (cfg as SlackChannelConfig).webhook_url || '';
  if (type === 'telegram') {
    next.bot_token = (cfg as TelegramChannelConfig).bot_token || '';
    next.chat_id = (cfg as TelegramChannelConfig).chat_id || '';
  }
  return next;
}

export default function CreateEditAlertChannel() {
  const { modalState, toggleModal } = useModalState('alert-channel');
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const close = useCallback(() => toggleModal(false), [toggleModal]);
  const { guardedClose, setDirty, setSubmitted } = useDirtyFormGuard(close);

  const { data: entity, isLoading, refetch } = useQuery({
    queryKey: ['alert-channel', modalState.id],
    queryFn: () => alertChannelsService.getById(modalState.id!),
    enabled: !!modalState.id && modalState.open,
  });

  const isEdit = modalState.reason === 'Update' && !!entity;
  const title = isEdit ? `Edit Alert Channel: ${entity?.name || entity?.id}` : 'Create Alert Channel';

  const [config, setConfig] = useState<EditableConfig>(DEFAULT_CONFIG);
  const [recipientInput, setRecipientInput] = useState('');
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (entity && modalState.open) {
      setConfig(configFrom(entity.type, entity.config));
    } else if (!modalState.id && modalState.open) {
      setConfig(DEFAULT_CONFIG);
    }
    setRecipientInput('');
  }, [entity, modalState.open, modalState.id]);

  useEffect(() => { if (modalState.id && modalState.open) refetch(); }, [modalState.id, modalState.open, refetch]);

  const addRecipient = () => {
    const email = recipientInput.trim();
    if (!email) return;
    if (!EMAIL_RE.test(email)) {
      enqueueSnackbar('Invalid email address', { variant: 'warning' });
      return;
    }
    if (config.recipients.includes(email)) {
      setRecipientInput('');
      return;
    }
    setConfig({ ...config, recipients: [...config.recipients, email] });
    setRecipientInput('');
  };

  const handleRecipientKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addRecipient();
    }
  };

  const removeRecipient = (email: string) => {
    setConfig({ ...config, recipients: config.recipients.filter((r) => r !== email) });
  };

  const buildConfig = (type: AlertChannelType): { config?: AlertChannelConfig; error?: string } => {
    if (type === 'email') {
      if (config.recipients.length === 0) return { error: 'Add at least one recipient email' };
      return { config: { recipients: config.recipients } };
    }
    if (type === 'slack') {
      if (!config.webhook_url.trim()) return { error: 'Webhook URL is required' };
      return { config: { webhook_url: config.webhook_url.trim() } };
    }
    // telegram
    if (!config.bot_token.trim()) return { error: 'Bot token is required' };
    if (!config.chat_id.trim()) return { error: 'Chat ID is required' };
    return { config: { bot_token: config.bot_token.trim(), chat_id: config.chat_id.trim() } };
  };

  const handleTest = async () => {
    if (!isEdit || !entity) return;
    setTesting(true);
    try {
      const result = await alertChannelsService.test(entity.id);
      if (result.ok) {
        enqueueSnackbar('Test message sent successfully', { variant: 'success' });
      } else {
        enqueueSnackbar(`Test failed: ${result.error || 'Unknown error'}`, { variant: 'error' });
      }
    } catch (err: any) {
      enqueueSnackbar(`Test failed: ${err?.response?.data?.error?.message || err.message || 'Unknown error'}`, { variant: 'error' });
    } finally {
      setTesting(false);
    }
  };

  const initialValues = (() => {
    if (!entity) return { name: '', type: 'email' as AlertChannelType, enabled: true, workspace_id: defaultCreateWorkspace() };
    return { name: entity.name, type: entity.type, enabled: entity.enabled, workspace_id: (entity as any).workspace_id ?? defaultCreateWorkspace() };
  })();

  return (
    <DraggableDialog open={modalState.open} onClose={guardedClose} maxWidth="sm" fullWidth title={isLoading ? 'Loading...' : title} titleId="alert-channel-dialog-title">
      {isLoading && modalState.id ? (
        <DialogContent><Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box></DialogContent>
      ) : (
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={schema}
          onSubmit={async (values, { setSubmitting, setStatus }) => {
            const { config: finalConfig, error } = buildConfig(values.type);
            if (error || !finalConfig) {
              setStatus(error || 'Invalid configuration');
              setSubmitting(false);
              return;
            }
            try {
              setSubmitted();
              const payload = {
                name: values.name,
                type: values.type,
                enabled: values.enabled,
                config: finalConfig,
              };
              if (isEdit) {
                await alertChannelsService.update(entity!.id, payload);
              } else {
                await alertChannelsService.create({ ...payload, workspace_id: values.workspace_id } as any);
              }
              queryClient.invalidateQueries({ queryKey: ['alert-channels'] });
              close();
              enqueueSnackbar('Saved', { variant: 'success' });
            } catch (err: any) {
              setStatus(err?.response?.data?.error?.message || err.message || 'Save failed');
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ submitForm, isSubmitting, status, values, setFieldValue }: { submitForm: () => Promise<void>; isSubmitting: boolean; status?: string; values: typeof initialValues; setFieldValue: (field: string, value: any) => void }) => (
            <>
              <FormDirtyTracker onDirtyChange={setDirty} />
              <DialogContent dividers>
                <CopyId id={entity?.id} show={isEdit} sx={{ mb: 1 }} />
                <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Name"
                    size="small"
                    fullWidth
                    value={values.name}
                    onChange={(e) => setFieldValue('name', e.target.value)}
                  />
                  {!isEdit && (
                    <WorkspaceField
                      value={(values as any).workspace_id}
                      onChange={(id) => setFieldValue('workspace_id', id)}
                    />
                  )}

                  <FormControl size="small" fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={values.type}
                      label="Type"
                      onChange={(e: SelectChangeEvent) => setFieldValue('type', e.target.value)}
                    >
                      {CHANNEL_TYPES.map((t) => (
                        <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormikSwitch name="enabled" label="Enabled" />

                  {/* Email recipients */}
                  {values.type === 'email' && (
                    <>
                      <Typography variant="subtitle2" sx={{ mt: 1 }}>Recipients</Typography>
                      <TextField
                        label="Add email"
                        size="small"
                        fullWidth
                        placeholder="name@example.com (press Enter to add)"
                        value={recipientInput}
                        onChange={(e) => setRecipientInput(e.target.value)}
                        onKeyDown={handleRecipientKeyDown}
                        onBlur={addRecipient}
                      />
                      {config.recipients.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {config.recipients.map((email) => (
                            <Chip key={email} label={email} size="small" onDelete={() => removeRecipient(email)} />
                          ))}
                        </Box>
                      )}
                    </>
                  )}

                  {/* Slack webhook */}
                  {values.type === 'slack' && (
                    <>
                      <Typography variant="subtitle2" sx={{ mt: 1 }}>Slack</Typography>
                      <TextField
                        label="Webhook URL"
                        size="small"
                        fullWidth
                        placeholder="https://hooks.slack.com/services/..."
                        value={config.webhook_url}
                        onChange={(e) => setConfig({ ...config, webhook_url: e.target.value })}
                      />
                    </>
                  )}

                  {/* Telegram */}
                  {values.type === 'telegram' && (
                    <>
                      <Typography variant="subtitle2" sx={{ mt: 1 }}>Telegram</Typography>
                      <TextField
                        label="Bot Token"
                        size="small"
                        fullWidth
                        type="password"
                        value={config.bot_token}
                        onChange={(e) => setConfig({ ...config, bot_token: e.target.value })}
                      />
                      <TextField
                        label="Chat ID"
                        size="small"
                        fullWidth
                        value={config.chat_id}
                        onChange={(e) => setConfig({ ...config, chat_id: e.target.value })}
                      />
                    </>
                  )}

                  {status && <Alert severity="error">{status}</Alert>}
                </Box>
              </DialogContent>
              <DialogActions>
                {isEdit && (
                  <Button
                    onClick={handleTest}
                    disabled={testing}
                    startIcon={testing ? <CircularProgress size={14} /> : <Send />}
                    sx={{ mr: 'auto' }}
                  >
                    {testing ? 'Sending...' : 'Send test'}
                  </Button>
                )}
                <Button onClick={guardedClose}>Cancel</Button>
                <Button variant="contained" onClick={submitForm} disabled={isSubmitting || !values.workspace_id}>
                  {isSubmitting ? <CircularProgress size={20} /> : 'Save'}
                </Button>
              </DialogActions>
            </>
          )}
        </Formik>
      )}
    </DraggableDialog>
  );
}
